/**
 * CommandCenter — Operator Command Center screen (FR-005 / FR-008 / FR-009).
 *
 * v2-design-parity layout that mirrors the Capicúa Front End Design v2-handoff
 * "hero" Command Center: 5-tile KPI row, spend-pulse + decisions-queue two-col,
 * a workspaces grid, and a recent-runs table.
 *
 * BACKEND-GAP FLAGGING
 * --------------------
 * The design implies several data points the production `operatorHome` backend
 * does NOT provide. Rather than fabricate them, every such element is wrapped in
 * <Unbacked> — a red dashed outline + native tooltip ("No backend data wired: …")
 * — so the gaps are visible in the UI. The flagged elements are:
 *   - "This week" time-range filter (no query/action)
 *   - KPI subtexts: "N in implementation", "N expire within the hour", alert text
 *   - KPI "About to Ship" (no ship/wave concept)
 *   - KPI "Spend / Month" total + monthly limit (only a 7-day daily series exists)
 *   - Decisions "closest deadline" countdown (no deadline field on tasks)
 *   - Workspace card: initiative count, description, member avatars, runs/wk
 *   - Recent-runs "Cost" column (no per-run cost field)
 *
 * Backed elements (greeting, pending-approvals, regressions, active-runs derived
 * count, recent-run status/name/elapsed, 7-day admin spend chart) render normally.
 */
import '../../styles/screens/today.css';
import React, { useCallback, useMemo } from "react";
import SubscriptionScope from "../../components/layout/SubscriptionScope";
import { useEffectiveUser } from "../../api/hooks/useEffectiveUser";
import { useOperatorHome } from "../../api/hooks/useOperatorHome";
import { useHashLocation } from "../../lib/router";
import { ConvexErrorBoundary } from "../../components/layout/ConvexErrorBoundary";
import SpendPulseChart from "./components/SpendPulseChart";
import { Crumb } from "../../components/primitives/visual/Crumb";
import { LiveDot } from "../../components/primitives/visual/LiveDot";
import { Button } from "../../components/primitives/Button";
import { Badge } from "../../components/primitives/Badge";
import { Icon } from "../../components/primitives/Icon";
import type {
  OperatorHomeView,
  RefinementRunCard,
  WorkspaceRoll,
} from "../../../../../shared/types/operatorHome";
import styles from "./CommandCenter.module.css";

// ─── Unbacked flag (no backend data / action) ──────────────────────────────

interface UnbackedProps {
  children: React.ReactNode;
  /** Plain-language description of what is missing, used in the tooltip. */
  reason: string;
  /** Use the block variant (outline inset + "NO BACKEND" corner tag). */
  block?: boolean;
  className?: string;
}

/**
 * Wraps an element the v2 design implies but the backend cannot supply.
 * Renders a red dashed outline + a native tooltip so the gap is visible.
 */
function Unbacked({ children, reason, block, className }: UnbackedProps): React.ReactElement {
  const cls = `${block ? styles.unbackedBlock : styles.unbacked}${className ? ` ${className}` : ""}`;
  return (
    <span className={cls} title={`No backend data wired: ${reason}`} data-unbacked="true">
      {children}
    </span>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const BADGE_STATUSES = new Set([
  "pending",
  "running",
  "complete",
  "failed",
  "waiting",
  "paused",
]);

type BadgeStatus = "pending" | "running" | "complete" | "failed" | "waiting" | "paused";

function toBadgeStatus(status: string): BadgeStatus | undefined {
  const key = status.trim().toLowerCase();
  return BADGE_STATUSES.has(key) ? (key as BadgeStatus) : undefined;
}

/** "RUN-XXXX" style short id derived from the Convex document id. */
function shortRunId(runId: string): string {
  const tail = runId.replace(/[^a-z0-9]/gi, "").slice(-4).toUpperCase();
  return tail ? `RUN-${tail}` : "RUN";
}

/** Elapsed wall-clock for a run, derived from createdAt/finishedAt. */
function formatElapsed(createdAt: number, finishedAt: number | null): string {
  const end = finishedAt ?? Date.now();
  const ms = Math.max(0, end - createdAt);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// ─── Outer Component (FR-008) ──────────────────────────────────────────────

/**
 * Outer CommandCenter provides <SubscriptionScope key={userId}> for clean
 * teardown on user change. Falls back to "anon" when userId is null.
 */
export default function CommandCenter(): React.ReactElement {
  const user = useEffectiveUser();
  const scopeKey = user?._id ?? "anon";

  return (
    <SubscriptionScope active key={scopeKey}>
      <CommandCenterBody />
    </SubscriptionScope>
  );
}

// ─── Inner Component (FR-005 / FR-009) ─────────────────────────────────────

function CommandCenterBody(): React.ReactElement {
  const state = useOperatorHome();
  const [, navigate] = useHashLocation();

  const handleRunClick = useCallback((runId: string) => {
    navigate(`/refine/${runId}`);
  }, [navigate]);

  // State 1: loading -> skeleton
  if (state.kind === "loading") {
    return <CommandCenterSkeleton />;
  }

  // State 2: error -> retry view
  if (state.kind === "error") {
    return (
      <div className={styles.container}>
        <p>{state.error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          aria-label="Retry"
        >
          Retry
        </button>
      </div>
    );
  }

  // State 3: forbidden -> soft empty state
  if (state.kind === "forbidden") {
    return (
      <div className={styles.forbidden}>
        <p>You do not have access to the Command Center.</p>
      </div>
    );
  }

  // States 4 & 5: ready (with or without partial banner)
  const { data } = state;
  const isAdmin = data.effectiveRole === "admin";

  const firstName = data.user?.firstName ?? data.user?.name ?? null;
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const greeting = firstName ? `Good ${partOfDay}, ${firstName}.` : `Good ${partOfDay}.`;

  return (
    <div className={styles.container}>
      <CommandHeader greeting={greeting} navigate={navigate} />

      {/* Section 1: KPI Row (5 tiles) */}
      <KpiRow data={data} navigate={navigate} />

      {/* Partial-data banner (FR-009 State 5) */}
      {data.partial && (
        <div className={styles.partialBanner} role="status">
          Some workspace data could not be loaded. Showing partial results.
        </div>
      )}

      {/* Section 2: Spend pulse + Decisions queue */}
      <SpendAndDecisions
        isAdmin={isAdmin}
        spendSeries={data.spendSeries}
        pendingTotal={data.kpis.pendingApprovalsTotal}
        navigate={navigate}
      />

      {/* Section 3: Workspaces grid */}
      <WorkspacesGrid workspaces={data.workspaces} navigate={navigate} />

      {/* Section 4: Recent runs table */}
      <RecentRunsTable
        runs={data.recentRuns}
        workspaces={data.workspaces}
        onRunClick={handleRunClick}
        navigate={navigate}
      />

      {/* Section 5: Changelog promo (static) */}
      <ChangelogPromo />
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function CommandHeader({
  greeting,
  navigate,
}: {
  greeting: string;
  navigate: (to: string) => void;
}): React.ReactElement {
  return (
    <div className={styles.header}>
      <Crumb label="Command Center" />
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.heading}>{greeting}</h1>
          <p className={styles.subhead}>
            Across all your workspaces, here&apos;s what needs your attention right now.
          </p>
        </div>
        <div className={styles.controls}>
          <LiveDot />
          <Unbacked reason="time-range filtering (no query/action)">
            <Button variant="pillOutline">This week</Button>
          </Unbacked>
          <Button variant="primary" icon="plus" onClick={() => navigate("/initiate")}>
            New initiative
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Row ─────────────────────────────────────────────────────────────

function KpiRow({
  data,
  navigate,
}: {
  data: OperatorHomeView;
  navigate: (to: string) => void;
}): React.ReactElement {
  // "Active runs" derived from the recent-runs slice (runs not yet finished).
  const activeRuns = data.recentRuns.filter((r) => r.finishedAt === null).length;
  const decisions = data.kpis.pendingApprovalsTotal;
  const qualityAlerts = data.kpis.regressionsTotal;

  return (
    <div className={styles.kpiRow}>
      {/* Active Runs — value derived; subtext has no backing */}
      <button type="button" className={styles.kpi} onClick={() => navigate("/activity")}>
        <span className={styles.kpiTag}>Active Runs</span>
        <span className={styles.kpiValue}>{activeRuns}</span>
        <span className={styles.kpiContext}>
          <Unbacked reason="run phase breakdown (e.g. 'in implementation')">
            run phase breakdown
          </Unbacked>
        </span>
      </button>

      {/* Decisions Pending — value backed; subtext (deadlines) not backed */}
      <button type="button" className={styles.kpi} onClick={() => navigate("/decisions")}>
        <span className={styles.kpiTag}>Decisions Pending</span>
        <span className={`${styles.kpiValue}${decisions > 0 ? ` ${styles.kpiValueCoral}` : ""}`}>
          {decisions}
        </span>
        <span className={styles.kpiContext}>
          <Unbacked reason="decision deadlines / expiry times">
            expiry times
          </Unbacked>
        </span>
      </button>

      {/* About to Ship — whole concept unbacked */}
      <Unbacked
        reason="ship / wave-delivery status (no such concept in the backend)"
        block
        className={styles.kpi}
      >
        <span className={styles.kpiTag}>About to Ship</span>
        <span className={styles.kpiValue}>—</span>
        <span className={styles.kpiContext}>Wave / delivery status</span>
      </Unbacked>

      {/* Spend / Month — no monthly total, no limit */}
      <Unbacked
        reason="monthly spend total and monthly limit (only a 7-day daily series exists)"
        block
        className={styles.kpi}
      >
        <span className={styles.kpiTag}>Spend / Month</span>
        <span className={styles.kpiValue}>—</span>
        <span className={styles.kpiContext}>of $— limit</span>
      </Unbacked>

      {/* Quality Alerts — count maps to regressions; subtext not backed */}
      <button type="button" className={styles.kpi} onClick={() => navigate("/activity")}>
        <span className={styles.kpiTag}>Quality Alerts</span>
        <span className={`${styles.kpiValue}${qualityAlerts > 0 ? ` ${styles.kpiValueCoral}` : ""}`}>
          {qualityAlerts}
        </span>
        <span className={styles.kpiContext}>
          <Unbacked reason="alert detail copy (e.g. 'cost up 41%')">
            alert detail
          </Unbacked>
        </span>
      </button>
    </div>
  );
}

// ─── Spend pulse + Decisions queue ─────────────────────────────────────────

function SpendAndDecisions({
  isAdmin,
  spendSeries,
  pendingTotal,
  navigate,
}: {
  isAdmin: boolean;
  spendSeries: { ts: number; amountUsd: number }[];
  pendingTotal: number;
  navigate: (to: string) => void;
}): React.ReactElement {
  const lastPoint = spendSeries.length > 0 ? spendSeries[spendSeries.length - 1] : null;
  const todayLabel = lastPoint ? `Today — $${lastPoint.amountUsd.toFixed(0)}` : "Today";

  return (
    <section className={styles.section}>
      <div className={styles.twoCol}>
        {/* Spend pulse (real, admin-only 7-day series) */}
        <div className={styles.cardBordered}>
          <div className={styles.cardRow}>
            <h2 className={styles.hFeature}>Spend pulse, last 7 days</h2>
            <span className={styles.mono}>USD</span>
          </div>
          {isAdmin ? (
            <>
              <ConvexErrorBoundary>
                <SpendPulseChart series={spendSeries} />
              </ConvexErrorBoundary>
              <div className={styles.spendFooter}>
                <span>7 days ago</span>
                <span>{todayLabel}</span>
              </div>
            </>
          ) : (
            <p className={styles.adminOnlyMessage}>Spend metrics are administrator-only.</p>
          )}
        </div>

        {/* Decisions queue (count backed; deadline not backed) */}
        <div className={styles.cardDark}>
          <span className={styles.monoCoral}>Decisions queue</span>
          <h2 className={`${styles.hFeature} ${styles.queueCount}`}>
            {pendingTotal} pending
          </h2>
          <p className={styles.queueLead}>
            Capicúa is paused on {pendingTotal} question{pendingTotal === 1 ? "" : "s"} across your
            workspaces.{" "}
            <Unbacked reason="closest decision deadline countdown">
              The closest deadline is in 00:00:00.
            </Unbacked>
          </p>
          <div className={styles.queueAction}>
            <Button variant="primary" iconRight="arrow-right" onClick={() => navigate("/decisions")}>
              Open the tray
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Workspaces grid ───────────────────────────────────────────────────────

function WorkspacesGrid({
  workspaces,
  navigate,
}: {
  workspaces: WorkspaceRoll[];
  navigate: (to: string) => void;
}): React.ReactElement {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.hSection}>Workspaces</h2>
        <Button variant="secondary" iconRight="arrow-right" onClick={() => navigate("/workspaces")}>
          See all
        </Button>
      </div>
      {workspaces.length === 0 ? (
        <div className={styles.cardBordered}>
          <p className={styles.wsDesc}>No workspaces with recent activity.</p>
        </div>
      ) : (
        <div className={styles.threeCol}>
          {workspaces.slice(0, 3).map((w) => (
            <button
              key={w.workspaceId}
              type="button"
              className={styles.wsCard}
              onClick={() => navigate("/workspaces")}
            >
              <Unbacked reason="active-initiative count per workspace">
                <span className={styles.mono}>— initiatives</span>
              </Unbacked>
              <h3 className={styles.wsName}>{w.workspaceName}</h3>
              <p className={styles.wsDesc}>
                <Unbacked reason="workspace description field">No description available</Unbacked>
              </p>
              <div className={styles.wsFooter}>
                <Unbacked reason="workspace member avatars / identities">
                  <span className={styles.mono}>members</span>
                </Unbacked>
                <Unbacked reason="runs-per-week metric">
                  <span className={styles.wsRuns}>— runs / wk</span>
                </Unbacked>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Recent runs table ─────────────────────────────────────────────────────

function RecentRunsTable({
  runs,
  workspaces,
  onRunClick,
  navigate,
}: {
  runs: RefinementRunCard[];
  workspaces: WorkspaceRoll[];
  onRunClick: (runId: string) => void;
  navigate: (to: string) => void;
}): React.ReactElement {
  const wsNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workspaces) m.set(w.workspaceId, w.workspaceName);
    return m;
  }, [workspaces]);

  const top = runs.slice(0, 5);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.hSection}>Recent runs</h2>
        <Button variant="secondary" iconRight="arrow-right" onClick={() => navigate("/activity")}>
          Open Activity
        </Button>
      </div>
      <div className={styles.tableCard} data-testid="recent-runs-list">
        <table className={styles.runsTable}>
          <thead>
            <tr>
              <th>Run</th>
              <th>Initiative</th>
              <th>Workspace</th>
              <th>Status</th>
              <th>Elapsed</th>
              <th className={styles.right}>Cost</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {top.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyRow}>
                  No recent refinement runs.
                </td>
              </tr>
            ) : (
              top.map((r) => {
                const badge = toBadgeStatus(r.status);
                return (
                  <tr key={r.runId} onClick={() => onRunClick(r.runId)}>
                    <td>
                      <span className={styles.num}>{shortRunId(r.runId)}</span>
                    </td>
                    <td className={styles.truncate}>{r.workflowName}</td>
                    <td>{wsNameById.get(r.workspaceId) ?? "—"}</td>
                    <td>
                      <Badge status={badge}>{r.status}</Badge>
                    </td>
                    <td className={styles.num}>{formatElapsed(r.createdAt, r.finishedAt)}</td>
                    <td className={`${styles.num} ${styles.right}`}>
                      <Unbacked reason="per-run cost (not tracked on runs)">—</Unbacked>
                    </td>
                    <td>
                      <Icon name="chevron-right" size={14} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Changelog promo (static) ──────────────────────────────────────────────

function ChangelogPromo(): React.ReactElement {
  return (
    <section className={styles.section}>
      <div className={styles.dfb}>
        <div>
          <span className={styles.monoCoral}>Wave 1.4 release</span>
          <h2>Parallel verification cuts your wait time roughly in half on long initiatives.</h2>
        </div>
      </div>
    </section>
  );
}

// ─── Skeleton (FR-009 State 1) ─────────────────────────────────────────────

function CommandCenterSkeleton(): React.ReactElement {
  return (
    <div className={styles.container}>
      <div className={styles.skeleton}>
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
      </div>
    </div>
  );
}
